import QuickDatePickLinks from "@/components/DateRangePicker/QuickDatePickLinks";
import StyledDateRangePicker from "@/components/DateRangePicker/StyledDateRangePicker";
import Icon from "@/components/Ui/Icon";
import { Button } from "@/components/shadcn/button";
import useOnClickOutside from "@/hooks/use-click-outside";
import { shortDateFormat } from "@/utils/numberFormat";
import { isBefore, isEqual } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { DateRange as DateRange17, Range as RangeDate } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

// React 18 TypeScript dance
const DateRange: any = DateRange17;

type Selection = {
  [key: string]: RangeDate;
};

type Props = {
  onChange: (val: RangeDate) => void;
  initDates?: { startDate: Date; endDate: Date };
};

const DateRangePicker = (props: Props) => {
  const { onChange, initDates } = props;

  const ref = useRef(null);

  const [ranges, setRanges] = useState<RangeDate[]>([
    {
      startDate: initDates?.startDate || new Date(),
      endDate: initDates?.endDate,
      key: "selection",
    },
  ]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [buttonText, setButtonText] = useState<string>(
    `dd/mm/yyyy - dd/mm/yyyy`,
  );

  const handleSelect = (ranges: Selection) => {
    const { selection } = ranges;
    const { startDate, endDate } = selection;
    if (startDate && endDate) {
      setButtonText(
        `${shortDateFormat(`${startDate}`)} - ${shortDateFormat(`${endDate}`)}`,
      );
      setRanges([selection]);
    }
  };

  const handleClickOutside = () => {
    if (!isOpen) return;

    setIsOpen(false);
    setRanges([
      {
        startDate: initDates?.startDate,
        endDate: initDates?.endDate,
        key: "selection",
      },
    ]);
    setButtonText(
      `${shortDateFormat(`${initDates?.startDate}`)} - ${shortDateFormat(
        `${initDates?.endDate}`,
      )}`,
    );
  };

  const handleConfirm = () => {
    const { startDate, endDate } = ranges[0];
    if (startDate && endDate) {
      if (isBefore(startDate, endDate) || isEqual(startDate, endDate)) {
        onChange(ranges[0]);
        setIsOpen(false);
      }
    }
  };

  const handleQuickSelect = (range: RangeDate) => {
    onChange(range);
    setIsOpen(false);
  };

  useEffect(() => {
    if (initDates) {
      const { startDate, endDate } = initDates;
      setButtonText(
        `${shortDateFormat(`${startDate}`)} - ${shortDateFormat(`${endDate}`)}`,
      );
      setRanges([{ startDate, endDate, key: "selection" }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initDates]);

  useOnClickOutside(ref, handleClickOutside);

  return (
    <>
      {isOpen && (
        <div className="fixed top-0 left-0 h-full w-full bg-black opacity-70" />
      )}
      <div className="relative" ref={ref}>
        <button
          className="bg-input/30 text-sx text-muted-foreground relative max-w-full rounded border px-5 py-2"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Select dates"
        >
          <div className="row flex-nowrap! space-x-5">
            <span className="w-full overflow-hidden font-medium text-ellipsis whitespace-nowrap">
              {buttonText}
            </span>
            <Icon
              className="text-muted-foreground text-xs"
              name={isOpen ? "chevron-up" : "chevron-down"}
            />
          </div>
        </button>
        {isOpen && (
          <div className="bg-card absolute top-full right-0 mt-3 rounded p-4">
            <StyledDateRangePicker>
              <DateRange
                ranges={ranges}
                displayMode="dateRange"
                months={2}
                direction="horizontal"
                onChange={handleSelect}
                dateDisplayFormat="P"
                showDateDisplay={false}
                maxDate={new Date()}
                minDate={new Date(2022, 3, 15)}
                rangeColors={["#282834"]}
                weekStartsOn={1}
                weekdayDisplayFormat="EEEEEE"
                monthDisplayFormat="MMMM"
              />
            </StyledDateRangePicker>
            <div className="border-border -mx-4 mb-5 border-t" />
            <div className="flex w-full items-center justify-between px-3">
              <QuickDatePickLinks selectFunc={handleQuickSelect} />
              <Button onClick={handleConfirm}>Apply dates</Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DateRangePicker;
